import { z } from 'zod'
import type { Product, ProductCategory } from '@/schemas/product.schema'
import { productSchema } from '@/schemas/product.schema'

export const CATEGORY_LABELS: Record<ProductCategory, string> = {
  buques: 'Buquês',
  vasos: 'Vasos',
  orquideas: 'Orquídeas',
  caixas: 'Caixas',
  maternidade: 'Maternidade',
  'para-ele': 'Para Ele',
  presentes: 'Presentes',
  assinatura: 'Coleção Assinatura',
}

export const CATEGORY_STORIES: Record<ProductCategory, string> = {
  buques:
    'O gesto mais antigo do mundo: flores frescas mudando de mãos — e um dia inteiro mudando de tom.',
  vasos: 'Uma pétala que fica em casa depois que a visita já foi embora.',
  orquideas: 'A flor que não se apressa — e por isso impressiona.',
  caixas:
    'Um jardim inteiro escondido numa caixa — e o perfume escapa antes de a tampa terminar de abrir.',
  maternidade: 'Flores do tamanho exato do começo de uma vida.',
  'para-ele':
    'Ele nunca ganha flores — e é exatamente por isso que nunca esquece quando ganha.',
  presentes:
    'Quando uma flor sozinha não é o bastante, e o gesto vem em dobro.',
  assinatura:
    'A grife da casa: peças desenhadas como joia, não apenas montadas como buquê.',
}

const rawProducts = [
  {
    slug: 'buque-amanda',
    name: 'Amanda',
    category: 'buques',
    priceInCents: 19800,
    shortStory: 'O jeito mais delicado de dizer "eu pensei em você" logo cedo.',
    occasions: ['sem-motivo', 'aniversario'],
    imagePath: null,
  },
  {
    slug: 'buque-amy',
    name: 'Amy',
    category: 'buques',
    priceInCents: 24800,
    shortStory:
      'Tons pastel amarrados à mão, do tamanho exato de uma saudade boa.',
    occasions: ['aniversario', 'sem-motivo'],
    imagePath: null,
  },
  {
    slug: 'buque-verona',
    name: 'Verona',
    category: 'buques',
    priceInCents: 29800,
    shortStory:
      'Um clássico italiano para declarar o que o silêncio já entregou.',
    occasions: ['namorados', 'aniversario'],
    imagePath: null,
  },
  {
    slug: 'buque-isabella',
    name: 'Isabella',
    category: 'buques',
    priceInCents: 34800,
    shortStory:
      'Camadas de pétalas para um sentimento que também cresce em camadas.',
    occasions: ['namorados', 'aniversario'],
    imagePath: null,
  },
  {
    slug: 'buque-kalita',
    name: 'Kalita',
    category: 'buques',
    priceInCents: 39800,
    shortStory: 'A elegância que antecede um "sim" sussurrado no ouvido certo.',
    occasions: ['namorados', 'casamento'],
    imagePath: null,
  },
  {
    slug: 'buque-love',
    name: 'Love',
    category: 'buques',
    priceInCents: 48000,
    shortStory:
      'Rosas vermelhas em espiral fechada, para o amor que já não pede licença.',
    occasions: ['namorados', 'casamento'],
    imagePath: null,
  },
  {
    slug: 'buque-ivy',
    name: 'Ivy',
    category: 'buques',
    priceInCents: 58000,
    shortStory:
      'Verdes e flores entrelaçados como uma promessa que ainda vai crescer.',
    occasions: ['casamento', 'corporativo'],
    imagePath: null,
  },
  {
    slug: 'buque-supreme-love',
    name: 'Supreme Love',
    category: 'buques',
    priceInCents: 78000,
    shortStory: 'O buquê que se ajoelha antes das palavras acontecerem.',
    occasions: ['casamento', 'aniversario', 'corporativo'],
    imagePath: null,
  },
  {
    slug: 'buque-valentina',
    name: 'Valentina',
    category: 'buques',
    priceInCents: 145800,
    shortStory:
      'A obra-prima da casa Flora, para o amor que não cabe em uma frase.',
    occasions: ['casamento', 'corporativo', 'aniversario'],
    imagePath: null,
  },
  {
    slug: 'vaso-jardim-de-ipe',
    name: 'Vaso Jardim de Ipê',
    category: 'vasos',
    priceInCents: 29800,
    shortStory:
      'Um pedaço do Cerrado florido para acordar qualquer cômodo da casa.',
    occasions: ['aniversario', 'sem-motivo'],
    imagePath: null,
  },
  {
    slug: 'vaso-alma-serena',
    name: 'Vaso Alma Serena',
    category: 'vasos',
    priceInCents: 48000,
    shortStory:
      'Um gesto de conforto para quem precisa sentir que não está sozinho.',
    occasions: ['condolencias'],
    imagePath: null,
  },
  {
    slug: 'vaso-boas-vindas',
    name: 'Vaso Boas-Vindas',
    category: 'vasos',
    priceInCents: 39800,
    shortStory:
      'Flores frescas para estrear um endereço novo com perfume de recomeço.',
    occasions: ['corporativo', 'sem-motivo'],
    imagePath: null,
  },
  {
    slug: 'vaso-ultimo-adeus',
    name: 'Vaso Último Adeus',
    category: 'vasos',
    priceInCents: 58000,
    shortStory:
      'Uma despedida dita em flores, para quem ficou marcado para sempre.',
    occasions: ['condolencias'],
    imagePath: null,
  },
  {
    slug: 'orquidea-phalaenopsis-aurora',
    name: 'Orquídea Phalaenopsis Aurora',
    category: 'orquideas',
    priceInCents: 78000,
    shortStory:
      'A elegância silenciosa que dura semanas e conquista à primeira vista.',
    occasions: ['aniversario', 'corporativo'],
    imagePath: null,
  },
  {
    slug: 'orquidea-cascata-branca',
    name: 'Orquídea Cascata Branca',
    category: 'orquideas',
    priceInCents: 118000,
    shortStory:
      'Uma cascata branca que transforma qualquer salão em cerimônia.',
    occasions: ['casamento', 'corporativo'],
    imagePath: null,
  },
  {
    slug: 'orquidea-mini-encanto',
    name: 'Orquídea Mini Encanto',
    category: 'orquideas',
    priceInCents: 58000,
    shortStory: 'Pequena no tamanho, inteira no encanto que carrega para casa.',
    occasions: ['sem-motivo', 'aniversario'],
    imagePath: null,
  },
  {
    slug: 'caixa-doce-surpresa',
    name: 'Caixa Doce Surpresa',
    category: 'caixas',
    priceInCents: 68000,
    shortStory: 'Uma caixa que se abre como um convite para sorrir sem aviso.',
    occasions: ['aniversario', 'sem-motivo'],
    imagePath: null,
  },
  {
    slug: 'caixa-prestigio-corporativa',
    name: 'Caixa Prestígio Corporativa',
    category: 'caixas',
    priceInCents: 98000,
    shortStory:
      'Flores nobres em caixa rígida, o gesto que chega à sala de reuniões antes de você.',
    occasions: ['corporativo'],
    imagePath: null,
  },
  {
    slug: 'caixa-serenidade',
    name: 'Caixa Serenidade',
    category: 'caixas',
    priceInCents: 78000,
    shortStory: 'Um abraço em flores para os dias em que as palavras faltam.',
    occasions: ['condolencias'],
    imagePath: null,
  },
  {
    slug: 'berco-de-petalas',
    name: 'Berço de Pétalas',
    category: 'maternidade',
    priceInCents: 58000,
    shortStory: 'Flores para celebrar os primeiros dias de uma vida inteira.',
    occasions: ['maternidade'],
    imagePath: null,
  },
  {
    slug: 'arranjo-primeiro-sorriso',
    name: 'Arranjo Primeiro Sorriso',
    category: 'maternidade',
    priceInCents: 48000,
    shortStory: 'Para comemorar o primeiro sorriso que muda tudo em casa.',
    occasions: ['maternidade'],
    imagePath: null,
  },
  {
    slug: 'arranjo-ninho-de-amor',
    name: 'Arranjo Ninho de Amor',
    category: 'maternidade',
    priceInCents: 68000,
    shortStory: 'Um ninho de flores para embalar os primeiros dias de vida.',
    occasions: ['maternidade'],
    imagePath: null,
  },
  {
    slug: 'buque-signature-ele',
    name: 'Buquê Signature Ele',
    category: 'para-ele',
    priceInCents: 48000,
    shortStory:
      'Folhagens escuras e flores em tons de vinho, com a sobriedade de um bom terno.',
    occasions: ['aniversario', 'sem-motivo'],
    imagePath: null,
  },
  {
    slug: 'arranjo-whisky-e-flor',
    name: 'Arranjo Whisky & Flor',
    category: 'para-ele',
    priceInCents: 98000,
    shortStory:
      'O âmbar do single malt ao lado do vermelho das rosas: dois clássicos na mesma bandeja.',
    occasions: ['aniversario', 'corporativo'],
    imagePath: null,
  },
  {
    slug: 'caixa-para-ele-executivo',
    name: 'Caixa Para Ele Executivo',
    category: 'para-ele',
    priceInCents: 78000,
    shortStory:
      'Eucalipto prateado e flores em tons de conhaque numa caixa de linhas retas, para a mesa de quem decide.',
    occasions: ['corporativo', 'sem-motivo'],
    imagePath: null,
  },
  {
    slug: 'cesta-doces-e-flores',
    name: 'Cesta Doces & Flores',
    category: 'presentes',
    priceInCents: 68000,
    shortStory:
      'Doces artesanais entre flores frescas, para adoçar duas vezes o mesmo dia.',
    occasions: ['aniversario', 'sem-motivo'],
    imagePath: null,
  },
  {
    slug: 'presente-completo-flora',
    name: 'Presente Completo Flora',
    category: 'presentes',
    priceInCents: 128000,
    shortStory:
      'Buquê, espumante e doces finos em uma só entrega: a celebração chega montada.',
    occasions: ['casamento', 'aniversario'],
    imagePath: null,
  },
  {
    slug: 'presente-pensando-em-voce',
    name: 'Presente Pensando em Você',
    category: 'presentes',
    priceInCents: 48000,
    shortStory:
      'Flores que tocam a campainha numa terça-feira qualquer, só para avisar que alguém lembrou.',
    occasions: ['sem-motivo'],
    imagePath: null,
  },
  {
    slug: 'edition-assinatura-flora-n1',
    name: 'Édition Assinatura Flora Nº1',
    category: 'assinatura',
    priceInCents: 145800,
    shortStory:
      'A primeira edição da grife: flores desenhadas e assinadas como alta joalheria.',
    occasions: ['casamento', 'aniversario'],
    imagePath: null,
  },
  {
    slug: 'edition-assinatura-flora-n2',
    name: 'Édition Assinatura Flora Nº2',
    category: 'assinatura',
    priceInCents: 118000,
    shortStory: 'Onde a alta-costura floral encontra o gesto perfeito.',
    occasions: ['corporativo', 'aniversario'],
    imagePath: null,
  },
  {
    slug: 'edition-assinatura-flora-n3',
    name: 'Édition Assinatura Flora Nº3',
    category: 'assinatura',
    priceInCents: 98000,
    shortStory: 'Uma peça de grife para quem não segue tendências, as cria.',
    occasions: ['sem-motivo', 'casamento'],
    imagePath: null,
  },
] as const

export const PRODUCTS: Product[] = z.array(productSchema).parse(rawProducts)
